/**
 * These Dialogs are part of the display for POS CFD
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { resetCFDrequest, sendCFDScreenNav, sendRetryPayment } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../Reducer'
import {
  getCalculatedCurrentCartValues,
  getCfdWebViewLoaded,
  getCurrentPaymentFlow,
  getIsPaired,
  getOrderUpdate,
  getPaymentProgress,
  getTransactionResult,
} from '../Selectors'
import { VNPusher } from '../../VNPusher/VNPusher'
import { getPusherKey, getPusherCluster } from '../../VNPusher/Selectors'

import { CFD_POS_PAYMENT_FLOWS, USER_PROGRESS } from '../Enums'

import { VNProgressCounter } from '../components/VNProgressCounter'
import { VNDialog } from '../components/VNDialog'

import {
  addPaymentToOrderInProgress,
  clearOrderInProgress,
  closePreAuthedRCOrder,
  startOfflineCardOrder,
  startOnlineCardOrder,
  updateSavedPayment,
} from '../../actions/order'
import { saveRichCheckoutOrderRequested } from '../../actions/richCheckout/order'
import { resetSafFullResponse } from '../../actions/peripheral'

import { resetCFD, setUserProgress } from '../ActionCreators'
import { getSafMode } from '../../selectors/appState'

import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { centsToDollars } from '../../utils/formatters'
import { INTERNAL_ORDER_STATES, isTabbed } from '../../utils/orderStates'
import { getOrderInProgress } from '../../selectors/order'
import { getBridgePaymentMethod } from '../../selectors/peripheral'
import useCloseFreeOrder from '../../hooks/useCloseFreeOrder'
import { RC_TIMEOUT_DURATION } from '../../screens/RichCheckout'
import { getIsPreAuthTab } from '../../utils/orderUtils'
import { useHistory } from 'react-router-dom'
import { getPrintingEnabled } from '../../selectors/printer'
import usePrinter from '../../VNPrinters/hooks/usePrinter'
import { getRoundUpForCharityFeatureEnabled } from '../../selectors/menus'
import useAuthStatus, { RECONNECT_MIN_APK_VERSION } from '../hooks/useAuthStatus'
import { isSDKCompatible } from '../../VNAndroidSDK/Utils'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { setSpinner } from '../../VNLoader/ActionCreators'
import { getSummitMode } from '../../VNMode/Selectors'

const SAVE_TEXT = {
  RETRY: 'RETRY',
  CANCEL: 'YES, CANCEL',
  CONFIRM_ORDER: 'CONFIRM ORDER',
}

const CANCEL_TEXT = {
  CANCEL: 'CANCEL',
  NEW_ORDER: 'NEW ORDER',
  DONT_CANCEL: 'NO, NEVER MIND',
}

export const CFDModalDialogs = ({
  clearCart,
  clearPromotionsOnOrder,
  CFDDialogDisplay,
  setCFDDialogDisplay,
  isRichCheckout,
  tipsEnabled,
  resetOrderTotals,
  resetCFDModalDialog,
  setResetCFDModalDialog,
}) => {
  // total number of steps on the POS during the process
  const [totalSteps, setTotalSteps] = useState(3)
  const timeoutRef = useRef()
  const reconnectTimerRef = useRef()
  const history = useHistory()

  // SELECTORS
  const pusherKey = useSelector(getPusherKey)
  const pusherCluster = useSelector(getPusherCluster)
  const paymentProgress = useSelector((state) => getPaymentProgress(state))
  const transactionResult = useSelector((state) => getTransactionResult(state))
  const orderUpdate = useSelector((state) => getOrderUpdate(state))
  const safMode = useSelector((state) => getSafMode(state))
  const calculatedCartValues = useSelector((state) => getCalculatedCurrentCartValues(state))
  const currentPaymentFlow = useSelector((state) => getCurrentPaymentFlow(state))
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const bridgePaymentMethod = useSelector((state) => getBridgePaymentMethod(state))
  const printingEnabled = useSelector((state) => getPrintingEnabled(state))

  // handle successful pairing and reconnects
  const isPaired = useSelector(getIsPaired)
  const isReady = useSelector(getCfdWebViewLoaded)
  const { attemptReconnect } = useAuthStatus()
  const prevIsPairedRef = useRef(isPaired)

  // reconnect/windeath logic refs
  const isRetryingPaymentRef = useRef(false)
  const isReconnectedRef = useRef(false)
  const reconnectRetriesRef = useRef(0)

  const curInvoiceNumber = useMemo(() => {
    if (!orderUpdate?.get('hRef')) return null
    if (orderUpdate?.get('invoiceNumber')) {
      return orderUpdate?.get('invoiceNumber')
    }
    return orderUpdate?.get('hRef')
  }, [orderUpdate])
  const androidSDKVersion = useSelector(getAndroidSDKVersion)
  // supported APK for reconnection logic
  const isSummitMode = useSelector(getSummitMode)
  const isSupportedAPK = useMemo(() => {
    if (isSummitMode) return false
    return isSDKCompatible(androidSDKVersion, RECONNECT_MIN_APK_VERSION)
  }, [androidSDKVersion, isSummitMode])
  const roundUpEnabled = useSelector((state) => getRoundUpForCharityFeatureEnabled(state, calculatedCartValues?.total))

  const networkAvailable = getNetworkAvailableValue()
  const isTabOrder = isTabbed(orderInProgress)
  const orderUuid = isTabOrder ? orderInProgress.uuid : calculatedCartValues.uuid
  const { printReceipt } = usePrinter(orderUuid)

  // LOCAL STATE
  // The error dialog display state
  const [showErrorDialog, setShowErrorDialog] = useState(false)

  // the cancel dialog display state
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  // display manual confirmation for rich checkout after timeout
  const [displayManualConfirm, setDisplayManualConfirm] = useState(false)

  // TODO: this should be a useMemo
  const currentModalTitle = useMemo(() => {
    if (isRichCheckout) {
      return USER_PROGRESS.RICH_CHECKOUT.CUSTOMER_SCANNING.title.replace(
        '{AMOUNT}',
        centsToDollars(calculatedCartValues?.total || 0),
      )
    }

    if (roundUpEnabled && !tipsEnabled) {
      return 'Customer Entering Donation on {AMOUNT}'.replace('{AMOUNT}', centsToDollars(calculatedCartValues?.total || 0))
    }

    if (roundUpEnabled) {
      return 'Customer Entering Tip  & Donation on {AMOUNT}'.replace('{AMOUNT}', centsToDollars(calculatedCartValues?.total || 0))
    }

    if (tipsEnabled) {
      return USER_PROGRESS.CARD_PAYMENT.CUSTOMER_TIP.title.replace('{AMOUNT}', centsToDollars(calculatedCartValues?.total || 0))
    }

    return 'Customer Confirming Total'
  }, [isRichCheckout, tipsEnabled, calculatedCartValues?.total, roundUpEnabled])

  // the title of the modal, changes depending on the situation
  const [modalTitle, setModalTitle] = useState('')

  // what step of the progress are on we?
  const [currentStep, setCurrentStep] = useState(1)

  const Pusher = useRef()
  const dispatch = useDispatch()
  const { closeFreeOrder } = useCloseFreeOrder()

  // USE EFFECTS

  useEffect(() => {
    if (resetCFDModalDialog === true) {
      setResetCFDModalDialog(false)
      setCurrentStep(1)
      setModalTitle(currentModalTitle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCFDModalDialog])

  const onFailedSocketHandler = (err) => {
    if (!Pusher.current) return

    const displayErrorMessage = err?.error?.data?.message || err?.message || 'An unknown error occurred.'
    setFallbackRichModal(`Please manually confirm your order.\n${displayErrorMessage}`)
  }

  // pusher socket for rich checkout
  useEffect(() => {
    if (isRichCheckout && networkAvailable) {
      Pusher.current = new VNPusher(pusherKey, pusherCluster, onFailedSocketHandler)
    }

    dispatch(resetSafFullResponse())

    return () => {
      if (Pusher.current) {
        Pusher.current.resetSocket()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isRichCheckout && CFDDialogDisplay && networkAvailable) {
      Pusher.current = new VNPusher(pusherKey, pusherCluster, onFailedSocketHandler)
      Pusher.current.subscribeDeviceChannel()
      Pusher.current.bindOrderTransfered(onBind)
      Pusher.current.bindOrderCreated(onOrderCreated)
    }

    if (isRichCheckout) {
      setTotalSteps(2)
    } else {
      setTotalSteps(3)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CFDDialogDisplay])

  const onBind = () => {
    setModalTitle(
      USER_PROGRESS.RICH_CHECKOUT.CUSTOMER_PAYING.title.replace('{AMOUNT}', centsToDollars(calculatedCartValues?.total)),
    )
    setCurrentStep(USER_PROGRESS.RICH_CHECKOUT.CUSTOMER_PAYING.index)
    sendCFDScreenNav(CFD_SCREEN.RICH_CHECKOUT_FINISH_TRANSACTION_ON_PHONE)
  }

  const onOrderCreated = () => {
    displayRichCheckoutReceipt()

    if (getIsPreAuthTab(orderInProgress)) {
      dispatch(closePreAuthedRCOrder({ uuid: orderInProgress.uuid }))
      return
    }

    dispatch(
      saveRichCheckoutOrderRequested({
        uuid: calculatedCartValues.uuid,
        orderNumber: calculatedCartValues.orderNumber,
        redirect: false,
      }),
    )
  }

  const setFallbackRichModal = (message = null) => {
    if (
      modalTitle === USER_PROGRESS.RICH_CHECKOUT.PAYMENT_FINISHED.TRANSACTION_COMPLETE.title ||
      modalTitle === USER_PROGRESS.RICH_CHECKOUT.PAYMENT_FINISHED.VIEWING_RECEIPT.title
    ) {
      return
    }

    if (!CFDDialogDisplay && !showCancelDialog) return

    setModalTitle(message || "Please check guest's mobile receipt to confirm order.")
    setCurrentStep(2)
    setDisplayManualConfirm(true)
    sendCFDScreenNav(CFD_SCREEN.RICH_CHECKOUT_SHOW_RECEIPT)
  }

  useEffect(() => {
    if (!isRichCheckout) return

    // if not online or if we are stuck in a step when pusher is not sending completion events
    // Pusher is not sending completion events for tabs created in another device
    if (!networkAvailable || currentStep >= USER_PROGRESS.RICH_CHECKOUT.CUSTOMER_PAYING.index) {
      timeoutRef.current = setTimeout(setFallbackRichModal, RC_TIMEOUT_DURATION)
    }

    return () => {
      clearTimeout(timeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRichCheckout, networkAvailable, CFDDialogDisplay, currentStep])

  useEffect(() => {
    setModalTitle(currentModalTitle)
  }, [currentModalTitle])

  useEffect(() => {
    if (!paymentProgress || paymentProgress?.size === 0) {
      return
    }

    if (paymentProgress?.error) {
      setShowErrorDialog(true)
      return
    }

    if (paymentProgress?.title || paymentProgress?.index) {
      let title = paymentProgress?.title

      if (paymentProgress.index === USER_PROGRESS.CARD_PAYMENT.CUSTOMER_TIP.index) {
        title = currentModalTitle
      }

      // an amount can be present on the paymentProgress, either use that or the calculatedCartValues
      setCurrentStep(parseInt(paymentProgress?.index))
      setModalTitle(title.replace('{AMOUNT}', paymentProgress?.amount || centsToDollars(calculatedCartValues?.total)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProgress])

  useEffect(() => {
    // prevent starting simple order if in tab authorization flow
    if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION) return

    // need to have this data before we can proceed
    // make sure we are only proceeding once per transaction
    if (orderUpdate && transactionResult && !orderUpdate.isEmpty() && !transactionResult.isEmpty()) {
      if (calculatedCartValues.isFinalizingTip) {
        dispatch(
          updateSavedPayment({
            tipAmountInCents: parseInt(orderUpdate.get('tipAmount')),
            charityAmountInCents: parseInt(orderUpdate.get('charityAmountInCents')),
          }),
        )
        return
      }

      let tempTransactionResult = transactionResult.toJS()
      delete tempTransactionResult.uuid

      const cardOrderObj = {
        cardReaderData: {
          ...calculatedCartValues,
          ...tempTransactionResult,
          authResponseData: {
            ...tempTransactionResult.authResponse,
          },
        },
        tipAmountInCents: parseInt(orderUpdate.get('tipAmount')),
        charityAmountInCents: parseInt(orderUpdate.get('charityAmountInCents')),
      }

      if (!isRichCheckout) {
        // checking everything so we can ensure we are in offline mode
        if (
          safMode === 2 ||
          !networkAvailable ||
          tempTransactionResult.isOfflineApproval ||
          transactionResult.isOfflineApproval
        ) {
          dispatch(startOfflineCardOrder(cardOrderObj))
        } else {
          dispatch(startOnlineCardOrder(cardOrderObj))
        }
      }
    } else if (orderUpdate && !orderUpdate.isEmpty() && currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.UNSET && CFDDialogDisplay) {
      // we are going to have a tip, so we need to start this order with the platform
      // only send if the modal dialog is actually open.
      let cardOrderObj = {
        cardReaderData: {
          ...calculatedCartValues,
        },
        orderState: INTERNAL_ORDER_STATES.PREAUTH,
        tipAmountInCents: parseInt(orderUpdate.get('tip') ?? orderUpdate.get('tipAmount', 0)),
        charityAmountInCents: parseInt(orderUpdate.get('charityAmountInCents', 0)),
      }

      // free order
      if (parseInt(orderUpdate?.get('amount')) === 0) {
        closeFreeOrder({ orderUuid: orderUpdate?.get('uuid'), isTabOrder })
        return
      }

      dispatch(startOnlineCardOrder(cardOrderObj))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionResult, orderUpdate])

  useEffect(() => {
    if (!printingEnabled || isRichCheckout) return

    if (CFDDialogDisplay && currentStep === USER_PROGRESS.CARD_PAYMENT.PAYMENT_FINISHED.VIEWING_RECEIPT.index) {
      printReceipt(false, false, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    if (!CFDDialogDisplay) return
    if (!isSupportedAPK) return
    if (paymentProgress?.title !== USER_PROGRESS.CARD_PAYMENT.ERROR.title) return

    if (isPaired && isReady) {
      dispatch(setSpinner(false))
      return
    }
    dispatch(setSpinner(true, 'Reconnecting'))
    return () => {
      dispatch(setSpinner(false))
    }
  }, [dispatch, isSupportedAPK, CFDDialogDisplay, isPaired, isReady, paymentProgress?.title])

  // keep track of reconnects
  // if attendant is stuck on "Paying Card" screen and we detect a reconnection
  useEffect(() => {
    if (!CFDDialogDisplay) return
    if (paymentProgress?.title !== USER_PROGRESS.CARD_PAYMENT.PAYING_CARD.title) return
    if (!isSupportedAPK) return
    if (isRetryingPaymentRef.current) {
      isRetryingPaymentRef.current = false
      return
    }

    if (!prevIsPairedRef.current && isPaired && isReady) {
      // Reconnection detected
      isReconnectedRef.current = true
    }

    prevIsPairedRef.current = isPaired && isReady

    // stop here if we are not reconnected
    if (!isPaired || !isReconnectedRef.current || !isReady) return

    // make sure there's not already a timer set
    if (reconnectTimerRef.current) return

    reconnectTimerRef.current = attemptReconnect({
      curInvoiceNumber,
      reconnectTimerRef,
      reconnectRetriesRef,
      isReconnectedRef,
    })

    return () => {
      clearTimeout(reconnectTimerRef?.current)
      reconnectTimerRef.current = null
      isReconnectedRef.current = false
      dispatch(setSpinner(false))
    }
  }, [CFDDialogDisplay, attemptReconnect, curInvoiceNumber, dispatch, isPaired, isReady, isSupportedAPK, paymentProgress?.title])

  const clearItemsAndPromotions = () => {
    clearCart()
    clearPromotionsOnOrder()
  }

  const resetCFDModal = (screen) => {
    softReset(screen)
    clearItemsAndPromotions()
    clearTimeout(timeoutRef.current)
  }

  const softReset = (screen, isCancelled) => {
    setCFDDialogDisplay(false)
    dispatch(resetCFD(true, true))
    resetOrderTotals(isCancelled)

    if (screen) {
      sendCFDScreenNav(screen)
    }

    setShowCancelDialog(false)
    setShowErrorDialog(false)
    setDisplayManualConfirm(false)
    dispatch(setUserProgress(USER_PROGRESS.CARD_PAYMENT.CUSTOMER_TIP))

    // This is required because MUI has a transition that will allow inputs to still happen.
    setTimeout(() => {
      setCurrentStep(1)
      setModalTitle(currentModalTitle)
    }, 1000)

    if (isRichCheckout && networkAvailable) {
      Pusher.current.unsubscribeDeviceChannel()
    }
  }

  const handleCFDProcessingSave = (screen) => {
    if (
      (!isRichCheckout && currentStep >= totalSteps) ||
      (isRichCheckout && !networkAvailable && currentStep > 1) ||
      (isRichCheckout && currentStep > totalSteps)
    ) {
      if (isTabOrder) {
        dispatch(clearOrderInProgress())
      }
      resetCFDModal(CFD_SCREEN.IDLE)
      history.replace({ ...history.location, state: {} })
    } else {
      history.replace({ ...history.location, state: {} })
      setShowCancelDialog(true)
      setCFDDialogDisplay(false)
    }
  }

  const saveButtonText = () => {
    if (isRichCheckout) {
      return currentStep > totalSteps ? 'NEW ORDER' : 'CANCEL'
    }

    if (currentStep === 2) {
      return ''
    }

    return currentStep >= totalSteps ? 'NEW ORDER' : 'CANCEL'
  }

  const displayRichCheckoutReceipt = () => {
    setDisplayManualConfirm(false)
    setModalTitle(USER_PROGRESS.RICH_CHECKOUT.PAYMENT_FINISHED.TRANSACTION_COMPLETE.title)
    setCurrentStep(USER_PROGRESS.RICH_CHECKOUT.PAYMENT_FINISHED.TRANSACTION_COMPLETE.index)
    resetCFDrequest()
  }

  const onManualConfirmOrder = () => {
    if (!isRichCheckout) return

    if (isTabOrder) {
      const tenderAmountInCents = orderInProgress.amountInCents
      const paymentType = 'mobile_app'
      const shortDescription = 'App'

      if (getIsPreAuthTab(orderInProgress)) {
        dispatch(closePreAuthedRCOrder({ uuid: orderInProgress.uuid }))
        displayRichCheckoutReceipt()
        return
      }

      dispatch(addPaymentToOrderInProgress({ tenderAmountInCents, paymentType, shortDescription, isRichCheckout: true }))
      displayRichCheckoutReceipt()
      return
    }

    dispatch(
      saveRichCheckoutOrderRequested({
        uuid: calculatedCartValues.uuid,
        orderNumber: calculatedCartValues.orderNumber,
        redirect: false,
      }),
    )
    displayRichCheckoutReceipt()
  }

  const showPrintButton = () => {
    if (!printingEnabled || isRichCheckout) return

    if (currentStep === USER_PROGRESS.CARD_PAYMENT.PAYMENT_FINISHED.VIEWING_RECEIPT.index) {
      return 'PRINT RECEIPT'
    }
  }

  const displayCFDModal = () => {
    if (isRichCheckout && displayManualConfirm) {
      return (
        <VNDialog
          open={CFDDialogDisplay}
          subTitle={modalTitle}
          image={<VNProgressCounter count={currentStep} total={2} />}
          showTextfield={false}
          closeOnPrimaryButtonClicked={false}
          primaryButtonText={currentStep >= 2 ? SAVE_TEXT.CONFIRM_ORDER : 'CANCEL'}
          secondaryButtonText={displayManualConfirm && currentStep === 2 ? CANCEL_TEXT.CANCEL : null}
          onSecondaryButtonClick={() => {
            dispatch(clearCart())
            dispatch(clearPromotionsOnOrder())
            softReset(CFD_SCREEN.CART)
          }}
          onPrimaryButtonClick={onManualConfirmOrder}
          fullWidth
        />
      )
    }

    return (
      <div>
        <VNDialog
          open={CFDDialogDisplay}
          subTitle={modalTitle}
          image={<VNProgressCounter count={currentStep} total={totalSteps} />}
          showTextfield={false}
          closeOnPrimaryButtonClicked={false}
          primaryButtonCountdownTime={currentStep > totalSteps ? 10 : undefined}
          primaryButtonText={saveButtonText()}
          secondaryButtonText={showPrintButton()}
          onSecondaryButtonClick={() => printReceipt()}
          onPrimaryButtonClick={
            currentStep !== USER_PROGRESS.CARD_PAYMENT.PAYING_CARD.index || isRichCheckout ? handleCFDProcessingSave : null
          }
          fullWidth
          alignTop={true}
        />
      </div>
    )
  }

  return (
    <>
      {displayCFDModal()}
      <VNDialog
        open={showCancelDialog}
        subTitle={'This will navigate you and the customer back to the cart.'}
        title={'Cancel Transaction?'}
        showTextfield={false}
        closeOnPrimaryButtonClicked={false}
        primaryButtonText={SAVE_TEXT.CANCEL}
        onPrimaryButtonClick={() => {
          softReset(CFD_SCREEN.CART, true)

          // If a user cancels a Rich Checkout Order don't send cancelled to stadium
          // else the order will go to incomplete because
          // Mobile Ordering wouldn't be able to handle a cancelled order.
          if (isRichCheckout) {
            // reset route so that RC doesn't start up again
            history.push('/concession-order')
            return
          }

          if (isTabOrder) return
          // do not create a cancelled order if the user backs out and A3700 has not gone to BP
          if (currentStep === 1) return
        }}
        secondaryButtonText={CANCEL_TEXT.DONT_CANCEL}
        onSecondaryButtonClick={() => {
          setShowCancelDialog(false)
          setCFDDialogDisplay(true)
        }}
        fullWidth
      />
      <VNDialog
        open={showErrorDialog}
        title={paymentProgress?.title}
        subTitle={paymentProgress?.subTitle}
        showTextfield={false}
        primaryButtonText={SAVE_TEXT.RETRY}
        closeOnPrimaryButtonClicked={false}
        onPrimaryButtonClick={() => {
          setShowErrorDialog(false)
          isRetryingPaymentRef.current = true
          sendRetryPayment(bridgePaymentMethod)
        }}
        secondaryButtonText={CANCEL_TEXT.CANCEL}
        onSecondaryButtonClick={() => {
          softReset(CFD_SCREEN.CART, true)
        }}
        fullWidth
      />
    </>
  )
}
